import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  presentationButtonOverlayVariants,
  presentationButtonVariants,
  presentationScoreCalculationTooltipVariants,
} from '../animation';
import { Scorecard, displayScorecardElementName, getIsBeachMode, ScorecardElementSymbol } from '../../../../Scorecard';
import {
  ScoreCalculationTooltipBody,
  type CalculationScorecardValues,
  scoreCalculationTooltipCalculationScorecardValuesFromScorecard,
  StandaloneScorecardElement,
  StandaloneScorecardElementProps,
} from '../../../components';
import { usePresentationViewOptionsContext } from '../context';
import { Link } from '@remix-run/react';

interface PresentationScorecardButtonProps extends StandaloneScorecardElementProps {
  hoveredElement: ScorecardElementSymbol | undefined;
  setHoveredElement: Dispatch<SetStateAction<ScorecardElementSymbol | undefined>>;
  to?: string;
  scorecard?: Scorecard;
  onClick?: () => void;
  elementOverride?: JSX.Element;
  btIsNull?: boolean;
  estateStatusChecklistItems?: CalculationScorecardValues['estateStatusChecklistItems'];
}

export const PresentationScorecardButton: FC<PresentationScorecardButtonProps> = ({
  to,
  value,
  element,
  className,
  scorecard,
  hoveredElement,
  setHoveredElement,
  onClick = () => {},
  elementOverride,
  estateStatusChecklistItems,
}) => {
  const { t: tScorecard } = useTranslation('scorecard');
  const { viewOptions } = usePresentationViewOptionsContext();

  const invisible = !viewOptions?.focusedElements?.includes(element);
  const elementName = displayScorecardElementName(element, tScorecard).replace(/\s+/g, ' ');
  const hovered = element === hoveredElement;
  const animation = hovered ? 'hovered' : hoveredElement === undefined ? 'idle' : 'receded';
  const beachMode = getIsBeachMode(scorecard);
  const beachModeElement =
    beachMode && (element === 'Bt' || element === 'Lt' || element === 'Qt' || element === 'Rt' || element === 'Tt');
  const handleClick = () => {
    setHoveredElement(undefined);
    onClick?.();
  };

  return (
    <>
      <motion.div
        initial="idle"
        animate={animation}
        variants={presentationButtonVariants}
        onHoverStart={() => setHoveredElement(element)}
        onHoverEnd={() => setHoveredElement(undefined)}
        className={clsx(className, 'relative z-[0]', to ? 'cursor-pointer' : 'cursor-default')}
        onClick={to ? undefined : handleClick}
      >
        {to ? (
          <Link
            aria-label={tScorecard('open-element-details-label', { element: elementName })}
            className="absolute inset-0 z-20 rounded-[20px]"
            onClick={handleClick}
            to={to}
          />
        ) : null}
        <motion.div
          initial="idle"
          animate={animation}
          variants={presentationButtonOverlayVariants}
          className="pointer-events-none absolute inset-0 left-[-1px] bottom-[1px] z-10 h-[101%] w-[101%] rounded-[20px] bg-darkMode-almostBlack/50"
        />
        <AnimatePresence>
          {hovered && (
            <motion.div
              exit="idle"
              initial="idle"
              animate={animation}
              variants={presentationScoreCalculationTooltipVariants}
              className={`absolute ${
                beachModeElement
                  ? 'top-[calc(100%_-_144px)] origin-center w-fit'
                  : 'top-[calc(100%_+_8px)] left-0 origin-top-left'
              } pointer-events-none`}
            >
              <ScoreCalculationTooltipBody
                isFullscreen={true}
                viewOptions={viewOptions}
                beachMode={beachMode}
                score={value}
                element={element}
                className="dark:!bg-darkMode-gray-floatingMenu"
                calculationScorecardValues={{
                  ...scoreCalculationTooltipCalculationScorecardValuesFromScorecard(scorecard),
                  estateStatusChecklistItems,
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {elementOverride ?? (
          <StandaloneScorecardElement
            value={value}
            element={element}
            beachMode={beachMode}
            className={clsx(
              invisible && 'grayscale opacity-40 !bg-darkMode-gray-medium',
              '!h-[216px] !w-[216px] !rounded-[20px] !px-[21.35px] !shadow-none',
              '[&_.StandaloneScorecardElement-element]:!text-[50.233px] [&_.StandaloneScorecardElement-element]:!leading-[53.046px]',
              '[&_.StandaloneScorecardElement-element-name]:!text-[22.539px] [&_.StandaloneScorecardElement-element-name]:!leading-[22.102px] [&_.StandaloneScorecardElement-element-name]:!tracking-[-0.232px]',
              '[&_.StandaloneScorecardElement-score-container]:!h-[124px]',
              '[&_.StandaloneScorecardElement-score]:!text-[70.326px] [&_.StandaloneScorecardElement-score]:!tracking-[-1.256px]',
              '[&_.StandaloneScorecardElement-score-percentage]:!text-[70.326px] [&_.StandaloneScorecardElement-score-percentage]:!tracking-[-1.256px]'
            )}
          />
        )}
      </motion.div>
    </>
  );
};
